import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import {atomPanelElevation} from '@s-ui/react-atom-panel'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMount} from '@s-ui/react-hooks'
import MoleculeAvatar, {moleculeAvatarBadgeSizes} from '@s-ui/react-molecule-avatar'
import MoleculeModal, {moleculeModalSizes} from '@s-ui/react-molecule-modal'
import MoleculeNotification, {
  moleculeNotificationAlignItems,
  moleculeNotificationAutoClose,
  moleculeNotificationBorderSizes,
  moleculeNotificationPosition,
  moleculeNotificationTypes,
  moleculeNotificationVariations
} from '@s-ui/react-molecule-notification'
import {useLocation, useRouter} from '@s-ui/react-router'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import FormConsent from '@adv-mt/mt-form-consent'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import Layout from '@adv-mt/mt-layout-basic'
import ModalBasicContent from '@adv-mt/mt-modal-basic-content'
import NavigationRequireAuth from '@adv-mt/mt-navigation-require-auth'
import NewsletterSubscription from '@adv-mt/mt-newsletter-subscription'
import {PanelBasic} from '@adv-mt/mt-panel-basic'
import PanelCta, {panelCtaButtonPosition, panelCtaButtonWidth} from '@adv-mt/mt-panel-cta'
import IconCircleCheck from '@adv-mt/mt-svg-icons/lib/CircleCheck'
import IconCircleClose from '@adv-mt/mt-svg-icons/lib/CircleClose'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'
import IconForbidden from '@adv-mt/mt-svg-icons/lib/Forbidden'
import TitleBasic, {titleBasicSizes} from '@adv-mt/mt-title-basic'
import TrackingPageView from '@adv-mt/mt-tracking-page-view'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import LinkFactory from '../../../components/LinkFactory/index.js'
import {ROUTES} from '../../../config/index.js'
import {ExternalConsentsLinks} from './components/ExternalConsentsLinks/index.js'
import {PanelOptionWrapper} from './components/PanelOptionWrapper/index.js'
import UserProfileInfo from './components/UserProfileInfo/index.js'
import {
  BASE_CLASS,
  CLEAN_SESSION_TIMEOUT,
  CONSENTS_CLASS,
  CONSENTS_INNER_CLASS,
  FALLBACK_COLOR,
  INFO_AVATAR_CLASS,
  INFO_BUTTON_CLASS,
  INFO_CLASS,
  INFO_TITLE_CLASS,
  MOLECULE_MODAL_CONTENT_TEXT_CLASS,
  NOTIFICATION_TEXT_CLASS,
  NOTIFICATION_WRAPPER_CLASS,
  PANEL_CLASS,
  PRIVACY_ACTIONS_CLASS,
  SECTION_CLASS
} from './config.js'
import {getInitialProps} from './getInitialProps.js'

const UserProfile = ({helmet, pageName}) => {
  const [consentsStatus, setConsentsStatus] = useState()
  const [notification, setNotification] = useState()
  const [needsVerification, setNeedsVerification] = useState(false)
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false)
  const [isDeleteAccountLoading, setIsDeleteAccountLoading] = useState(false)
  const [isLoadingTakeout, setIsLoadingTakeout] = useState(false)

  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const {USER_PROFILE_EDIT_PATTERN} = config.get('page')
  const SITE_NAME = config.get('TENANT')
  const {
    USER_CONSENTS: {
      FINANCIAL_SERVICES: financialServicesConsentKey,
      WHATSAPP_COMMUNICATIONS_C2B: whatsappConsentKey,
      PORTAL_ANNOUNCEMENTS: portalAnnouncementsConsentKey,
      EXTERNAL_SERVICES_OFFERS: externalServicesOffersConsentKey,
      SEGMENTED_ADS: segmentedAdsConsentKey
    },
    ENABLED_USER_PROFILE_CONSENTS,
    DATA_MANAGEMENT_URL,
    CONSENT_EXTERNAL_SERVICES_INFO_URL,
    CONSENT_FINANCIAL_SERVICES_INFO_URL,
    CONSENT_SEGMENTED_ADS_INFO_URL,
    ACCOUNT_VERIFICATION_REMINDER_ENABLED
  } = config.get('user')

  const isFinancialServicesConsentEnabled = ENABLED_USER_PROFILE_CONSENTS.includes(financialServicesConsentKey)
  const isWhatsappConsentEnabled = ENABLED_USER_PROFILE_CONSENTS.includes(whatsappConsentKey)
  const isPortalAnnouncementsConsentEnabled = ENABLED_USER_PROFILE_CONSENTS.includes(portalAnnouncementsConsentKey)
  const isExternalServicesOffersConsentEnabled = ENABLED_USER_PROFILE_CONSENTS.includes(
    externalServicesOffersConsentKey
  )
  const isSegmentedAdsConsentEnabled = ENABLED_USER_PROFILE_CONSENTS.includes(segmentedAdsConsentKey)

  const hasWhatsappSection = isWhatsappConsentEnabled

  const {push} = useRouter()
  const {pathname} = useLocation()

  const {user: userData} = useContext(AuthenticationContext)
  const {avatar, email, name: userName, phone, province, isEmailConfirmed} = userData || {}

  const delayToNextFrame = useDelayToNextFrame()

  const {
    TRACKING_EVENT_NAMES,
    USER_CONSENTS_TRACKING_EVENT_NAMES_MAPPING,
    HIT_ORIGINS: {USER_PROFILE: hitOriginUserProfile}
  } = config.get('tracking')

  const {track} = useAnalyticsTrack()

  const handleClickChangeUserData = () => {
    push(USER_PROFILE_EDIT_PATTERN)
  }

  useMount(() => {
    const getUserConsents = async () => {
      const [consentsError, consentsResponse] = await domain.get('get_consents_user_use_case').execute()
      console.error(consentsError)
      setConsentsStatus(consentsResponse)
    }

    getUserConsents()
  })

  const MOLECULE_NOTIFICATION_PROPS = {
    alignItems: moleculeNotificationAlignItems.CENTER,
    position: moleculeNotificationPosition.relative,
    roundedCorners: moleculeNotificationBorderSizes.medium,
    showLeftIconMobile: true,
    variation: moleculeNotificationVariations.positive
  }

  const handleConsentError = error => {
    console.error(error)
    setNotification({
      message: 'COMMON.ERROR_HAPPENED_GENERIC',
      type: moleculeNotificationTypes.error,
      icon: <IconForbidden size={ATOM_ICON_SIZES.medium} />
    })
  }

  const handleConsentSuccess = ({consentKey}) => {
    setNotification({
      message: 'COMMON.SAVED_SUCCESS',
      type: moleculeNotificationTypes.success,
      icon: <IconCircleCheck size={ATOM_ICON_SIZES.medium} />
    })

    const isNewsletterConsent = consentKey === portalAnnouncementsConsentKey

    if (isNewsletterConsent) {
      runVerificationRequirements()
    }

    delayToNextFrame(() => {
      const status = consentsStatus.getConsentStatus({key: consentKey})
      const eventName = USER_CONSENTS_TRACKING_EVENT_NAMES_MAPPING[consentKey]

      domain
        .get('get_event_tracking_data_use_case')
        .execute({
          event: eventName,
          origin: hitOriginUserProfile,
          status
        })
        .then(consentClickedEvent => {
          track(consentClickedEvent)
        })
    })
  }

  const runVerificationRequirements = () => {
    const isNewsletterConsentEnabled =
      consentsStatus.getConsentStatus({key: portalAnnouncementsConsentKey}) && ACCOUNT_VERIFICATION_REMINDER_ENABLED
    setNeedsVerification(Boolean(isNewsletterConsentEnabled && !isEmailConfirmed))
  }

  const privacyActionsCommonProps = {
    panelBasicProps: {
      isHorizontalPaddingless: true,
      elevation: atomPanelElevation.NONE
    },
    cta: {
      width: panelCtaButtonWidth.SMALL,
      position: panelCtaButtonPosition.CENTER
    }
  }
  const privacyActions = [
    {
      ...privacyActionsCommonProps,
      title: i18n.t('USER_PROFILE.PRIVACY.ACTION.DATA_MANAGEMENT_TITLE'),
      description: i18n.t('USER_PROFILE.PRIVACY.ACTION.DATA_MANAGEMENT_DESCRIPTION'),
      cta: {
        ...privacyActionsCommonProps.cta,
        atomButtonProps: {
          size: atomButtonSizes.LARGE
        },
        href: DATA_MANAGEMENT_URL,
        link: true,
        text: i18n.t('USER_PROFILE.PRIVACY.ACTION.DATA_MANAGEMENT_CTA'),
        onClick: async () => {
          const {ACCOUNT_MANAGE_DATA_CLICKED} = TRACKING_EVENT_NAMES
          const trackingData = await domain
            .get('get_event_tracking_data_use_case')
            .execute({event: ACCOUNT_MANAGE_DATA_CLICKED})

          track(trackingData)
        }
      }
    },
    {
      ...privacyActionsCommonProps,
      title: i18n.t('USER_PROFILE.PRIVACY.ACTION.DATA_DOWNLOAD_TITLE'),
      description: i18n.t('USER_PROFILE.PRIVACY.ACTION.DATA_DOWNLOAD_DESCRIPTION', {email}),
      cta: {
        ...privacyActionsCommonProps.cta,
        text: i18n.t('USER_PROFILE.PRIVACY.ACTION.DATA_DOWNLOAD_CTA'),
        atomButtonProps: {
          isLoading: isLoadingTakeout,
          size: atomButtonSizes.LARGE
        },
        onClick: async () => {
          setIsLoadingTakeout(true)
          const [useCaseError] = await domain.get('request_takeout_data_user_use_case').execute()

          const {ACCOUNT_TAKEOUT_CLICKED} = TRACKING_EVENT_NAMES
          const trackingData = await domain
            .get('get_event_tracking_data_use_case')
            .execute({event: ACCOUNT_TAKEOUT_CLICKED})

          track(trackingData)

          if (useCaseError) {
            setNotification({
              autoClose: moleculeNotificationAutoClose.medium,
              message: 'USER_PROFILE.PRIVACY.ACTION.DATA_DOWNLOAD_ERROR',
              type: moleculeNotificationTypes.error,
              icon: <IconCircleClose size={ATOM_ICON_SIZES.medium} />
            })
          } else {
            setNotification({
              autoClose: moleculeNotificationAutoClose.long,
              message: 'USER_PROFILE.PRIVACY.ACTION.DATA_DOWNLOAD_SUCCESS',
              type: moleculeNotificationTypes.success,
              icon: <IconCircleCheck size={ATOM_ICON_SIZES.medium} />
            })
          }

          setIsLoadingTakeout(false)
        }
      }
    },
    {
      ...privacyActionsCommonProps,
      title: i18n.t('USER_PROFILE.PRIVACY.ACTION.DELETE_ACCOUNT_TITLE'),
      description: i18n.t('USER_PROFILE.PRIVACY.ACTION.DELETE_ACCOUNT_DESCRIPTION'),
      cta: {
        ...privacyActionsCommonProps.cta,
        atomButtonProps: {
          size: atomButtonSizes.LARGE
        },
        text: i18n.t('USER_PROFILE.PRIVACY.ACTION.DELETE_ACCOUNT_CTA'),
        onClick: async () => {
          setShowDeleteAccountModal(true)
        }
      }
    }
  ]

  const onDeleteConfirmClick = async () => {
    setIsDeleteAccountLoading(true)
    const [useCaseError] = await domain.get('delete_account_user_use_case').execute()

    if (useCaseError) {
      setIsDeleteAccountLoading(false)
      setNotification({
        message: 'USER_PROFILE.PRIVACY.ACTION.DELETE_ACCOUNT_ERROR',
        type: moleculeNotificationTypes.warning,
        icon: <IconCircleCheck size={ATOM_ICON_SIZES.medium} />
      })
    } else {
      setNotification({
        message: 'USER_PROFILE.PRIVACY.ACTION.DELETE_ACCOUNT_SUCCESS',
        type: moleculeNotificationTypes.success,
        icon: <IconCircleCheck size={ATOM_ICON_SIZES.medium} />
      })

      setTimeout(() => {
        window.location.href = ROUTES.LOGOUT
      }, CLEAN_SESSION_TIMEOUT)
    }
  }

  useEffect(() => {
    consentsStatus && runVerificationRequirements()
  }, [consentsStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showDeleteAccountModal) return

    const trackModalOpen = async () => {
      const {ACCOUNT_DELETE_CLICKED} = TRACKING_EVENT_NAMES
      const trackingData = await domain.get('get_event_tracking_data_use_case').execute({event: ACCOUNT_DELETE_CLICKED})

      track(trackingData)
    }

    delayToNextFrame(trackModalOpen)
  }, [showDeleteAccountModal]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <NavigationRequireAuth currentPath={pathname}>
      <Layout.Basic isPaddingless linkFactory={LinkFactory}>
        <Helmet {...helmet} />
        <div className={BASE_CLASS}>
          <PanelBasic>
            <div className={INFO_CLASS}>
              <div className={INFO_TITLE_CLASS}>
                <TitleBasic size={titleBasicSizes.m} title={i18n.t('USER_PROFILE.INFO.TITLE')} />
              </div>
              <div className={INFO_AVATAR_CLASS}>
                <MoleculeAvatar
                  src={avatar}
                  size={moleculeAvatarBadgeSizes.XXLARGE}
                  name={userName}
                  fallbackColor={FALLBACK_COLOR}
                />
                <TitleBasic size={titleBasicSizes.s} title={userName} />
              </div>
              <UserProfileInfo email={email} phone={phone} province={province} />
              <div className={INFO_BUTTON_CLASS}>
                <AtomButton
                  design={atomButtonDesigns.OUTLINE}
                  shape={atomButtonShapes.CIRCULAR}
                  onClick={handleClickChangeUserData}
                  size={atomButtonSizes.LARGE}
                >
                  {i18n.t('USER_PROFILE.INFO.CHANGE_BUTTON_TEXT')}
                </AtomButton>
              </div>

              <TrackingPageView pageName={pageName} />
            </div>
          </PanelBasic>
          <PanelBasic>
            <div className={PANEL_CLASS}>
              <TitleBasic
                size={titleBasicSizes.s}
                title={i18n.t('USER_PROFILE.NOTIFICATIONS.TITLE')}
                description={i18n.t(`USER_PROFILE.NOTIFICATIONS.DESCRIPTION_${SITE_NAME.toUpperCase()}`)}
              />
              <div className={SECTION_CLASS}>
                <TitleBasic size={titleBasicSizes.xs} title={i18n.t('USER_PROFILE.NOTIFICATIONS.SECTION_MAIL_TITLE')} />
                <div className={CONSENTS_CLASS}>
                  {isPortalAnnouncementsConsentEnabled && consentsStatus && (
                    <PanelOptionWrapper>
                      <div className={CONSENTS_INNER_CLASS}>
                        <FormConsent
                          consentsStatus={consentsStatus}
                          id={portalAnnouncementsConsentKey}
                          onError={handleConsentError}
                          onSuccess={() => handleConsentSuccess({consentKey: portalAnnouncementsConsentKey})}
                        />
                        {needsVerification && <NewsletterSubscription pageName={pageName} />}
                      </div>
                    </PanelOptionWrapper>
                  )}
                  {isExternalServicesOffersConsentEnabled && consentsStatus && (
                    <PanelOptionWrapper>
                      <FormConsent
                        consentsStatus={consentsStatus}
                        id={externalServicesOffersConsentKey}
                        onError={handleConsentError}
                        onSuccess={() => handleConsentSuccess({consentKey: externalServicesOffersConsentKey})}
                        moreInfoUrl={CONSENT_EXTERNAL_SERVICES_INFO_URL}
                      />
                    </PanelOptionWrapper>
                  )}
                  <ExternalConsentsLinks />
                </div>
              </div>
              <div className={SECTION_CLASS}>
                <TitleBasic size={titleBasicSizes.xs} title={i18n.t('USER_PROFILE.NOTIFICATIONS.SECTION_APP_TITLE')} />
                <div className={CONSENTS_CLASS}>
                  <ExternalConsentsLinks />
                </div>
              </div>
              {hasWhatsappSection && (
                <div className={SECTION_CLASS}>
                  <TitleBasic
                    size={titleBasicSizes.xs}
                    title={i18n.t('USER_PROFILE.NOTIFICATIONS.SECTION_WHATSAPP_TITLE')}
                  />
                  <div className={CONSENTS_CLASS}>
                    {isWhatsappConsentEnabled && consentsStatus && (
                      <PanelOptionWrapper>
                        <FormConsent
                          consentsStatus={consentsStatus}
                          id={whatsappConsentKey}
                          onError={handleConsentError}
                          onSuccess={() => handleConsentSuccess({consentKey: whatsappConsentKey})}
                        />
                      </PanelOptionWrapper>
                    )}
                  </div>
                </div>
              )}
            </div>
          </PanelBasic>
          <PanelBasic>
            <div className={PANEL_CLASS}>
              <TitleBasic
                size={titleBasicSizes.s}
                title={i18n.t('USER_PROFILE.PRIVACY.TITLE')}
                description={i18n.t(`USER_PROFILE.PRIVACY.DESCRIPTION_${SITE_NAME.toUpperCase()}`)}
              />
              <div className={SECTION_CLASS}>
                <div className={CONSENTS_CLASS}>
                  {isSegmentedAdsConsentEnabled && consentsStatus && (
                    <PanelOptionWrapper>
                      <FormConsent
                        consentsStatus={consentsStatus}
                        id={segmentedAdsConsentKey}
                        onError={handleConsentError}
                        onSuccess={() => handleConsentSuccess({consentKey: segmentedAdsConsentKey})}
                        moreInfoUrl={CONSENT_SEGMENTED_ADS_INFO_URL}
                      />
                    </PanelOptionWrapper>
                  )}
                  {isFinancialServicesConsentEnabled && consentsStatus && (
                    <PanelOptionWrapper>
                      <FormConsent
                        consentsStatus={consentsStatus}
                        id={financialServicesConsentKey}
                        onError={handleConsentError}
                        onSuccess={() => handleConsentSuccess({consentKey: financialServicesConsentKey})}
                        moreInfoUrl={CONSENT_FINANCIAL_SERVICES_INFO_URL}
                      />
                    </PanelOptionWrapper>
                  )}
                </div>

                <div className={PRIVACY_ACTIONS_CLASS}>
                  {privacyActions.map((panelProps, index) => (
                    <PanelCta key={`privacyAction-${index}`} {...panelProps} />
                  ))}
                </div>
              </div>
            </div>
          </PanelBasic>
          <div className={NOTIFICATION_WRAPPER_CLASS}>
            <MoleculeNotification
              {...MOLECULE_NOTIFICATION_PROPS}
              autoClose={notification?.autoClose}
              icon={notification?.icon}
              onClose={() => setNotification(null)}
              show={Boolean(notification)}
              type={notification?.type}
            >
              {notification && notification?.message && (
                <p className={NOTIFICATION_TEXT_CLASS}>
                  {htmlStringToReactElement(
                    i18n.t(notification.message, {highlightEnd: '</strong>', highlightStart: '<strong>'})
                  )}
                </p>
              )}
            </MoleculeNotification>
          </div>
        </div>
        <MoleculeModal
          isOpen={showDeleteAccountModal}
          size={moleculeModalSizes.MEDIUM}
          floatingIconClose
          onClose={() => setShowDeleteAccountModal(false)}
          iconClose={<IconClose size={ATOM_ICON_SIZES.medium} />}
        >
          <ModalBasicContent
            stepsContent={[
              {
                title: i18n.t('USER_PROFILE.PRIVACY.ACTION.DELETE_ACCOUNT_TITLE'),
                buttons: {
                  confirmButton: {
                    color: atomButtonColors.ERROR,
                    customCallBack: onDeleteConfirmClick,
                    isDisabled: false,
                    isLoading: isDeleteAccountLoading,
                    text: i18n.t('COMMON.REMOVE')
                  },
                  cancelButton: {
                    customCallBack: () => {
                      setShowDeleteAccountModal(false)
                    },
                    isDisabled: false,
                    text: i18n.t('COMMON.CANCEL')
                  }
                },
                children: (
                  <>
                    <p className={MOLECULE_MODAL_CONTENT_TEXT_CLASS}>
                      {i18n.t('USER_PROFILE.PRIVACY.ACTION.DELETE_ACCOUNT_MODAL_TEXT_1')}
                    </p>
                    <p className={MOLECULE_MODAL_CONTENT_TEXT_CLASS}>
                      {i18n.t('USER_PROFILE.PRIVACY.ACTION.DELETE_ACCOUNT_MODAL_TEXT_2')}
                    </p>
                  </>
                )
              }
            ]}
          />
        </MoleculeModal>
      </Layout.Basic>
    </NavigationRequireAuth>
  )
}

UserProfile.getInitialProps = getInitialProps

UserProfile.propTypes = {
  helmet: PropTypes.object,
  pageName: PropTypes.string
}

export default UserProfile
